"use client";

import React, { useRef, useState } from "react";
import { useController, useFormContext } from "react-hook-form";
import { Button } from "./forms/button";
import { useTranslations } from "next-intl";

interface VideoRecorderUploaderProps {
  name: string; // Field name for react-hook-form
  width?: number;
  height?: number;
  className?: string;
  responsive?: boolean;
  initialVideoUrl?: string; // URL from backend for preview
}

const VideoRecorderUploader = ({
  name,
  width = 409,
  height = 335,
  className = "",
  responsive = false,
  initialVideoUrl,
}: VideoRecorderUploaderProps) => {
  const t = useTranslations("videoRecorderUploader");
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const [wasCleared, setWasCleared] = useState(false);

  const [recording, setRecording] = useState(false);
  const [previewURL, setPreviewURL] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [hasInitialVideo, setHasInitialVideo] = useState(false);

  // Get control from form context
  const { control } = useFormContext();

  // Use useController to manage the field
  const {
    field: { value: uploadedVideoUrl, onChange },
  } = useController({
    name,
    control,
  });

  // Handle initial video URL from backend
  React.useEffect(() => {
    if (initialVideoUrl && !hasInitialVideo) {
      setPreviewURL(initialVideoUrl);
      setHasInitialVideo(true);
      setWasCleared(false);
    }
  }, [initialVideoUrl, hasInitialVideo]);

  // Timer effect for recording
  React.useEffect(() => {
    let interval: NodeJS.Timeout;

    if (recording) {
      interval = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } else {
      setRecordingTime(0);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [recording]);

  // Format time helper
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  // Function to clear current video (uploaded or recorded)
  const clearCurrentVideo = () => {
    if (previewURL && !initialVideoUrl) {
      // Only revoke URL if it's not the initial URL from backend
      URL.revokeObjectURL(previewURL);
    }

    setPreviewURL(null);
    setWasCleared(true);
    setHasInitialVideo(false);
    // Update react-hook-form
    onChange(null);

    if (videoRef.current) {
      videoRef.current.srcObject = null;
      videoRef.current.src = "";
      videoRef.current.controls = false;
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    const videoFile = files.find(
      (file) =>
        file.type.startsWith("video/") ||
        [".mp4", ".mpeg", ".webm"].some((ext) =>
          file.name.toLowerCase().endsWith(ext),
        ),
    );

    if (videoFile) {
      // Cancel any ongoing stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      // Stop recording if it was active
      if (recording) {
        mediaRecorderRef.current?.stop();
        setRecording(false);
      }

      // Clear any existing preview URL to avoid memory leaks
      if (previewURL) {
        URL.revokeObjectURL(previewURL);
      }

      const url = URL.createObjectURL(videoFile);
      setPreviewURL(url);
      setWasCleared(false);

      // Update react-hook-form
      onChange(videoFile);

      if (videoRef.current) {
        videoRef.current.srcObject = null;
        videoRef.current.src = url;
        videoRef.current.controls = true;
        videoRef.current.muted = false;
        videoRef.current.play();
      }
    } else {
      alert(t("invalidFileAlert"));
    }
  };

  const startRecording = async () => {
    try {
      // Clear any existing video to start fresh
      clearCurrentVideo();

      // 🔊 Request both video and audio
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      const chunks: Blob[] = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: "video/webm" }); // webm has better browser support with audio
        const url = URL.createObjectURL(blob);
        setPreviewURL(url);

        // Create a File object from the blob
        const recordedFile = new File([blob], "recorded-video.webm", {
          type: "video/webm",
        });

        // Stop camera and mic
        stream.getTracks().forEach((track) => track.stop());

        if (videoRef.current) {
          videoRef.current.srcObject = null;
          videoRef.current.src = url;
          videoRef.current.controls = true;
          videoRef.current.muted = false;

          // Add error handling for play()
          videoRef.current.play().catch((error) => {
            // This is normal when switching between streams
          });
        }
        setWasCleared(false);

        // Update react-hook-form
        onChange(recordedFile);

        mediaRecorderRef.current = null;
      };

      mediaRecorder.start();
      setRecording(true);
    } catch (err) {
      alert(t("permissionAlert"));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Cancel any ongoing stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    // Stop recording if it was active
    if (recording) {
      mediaRecorderRef.current?.stop();
      setRecording(false);
    }

    // Clear any existing preview URL to avoid memory leaks
    if (previewURL) {
      URL.revokeObjectURL(previewURL);
    }

    const url = URL.createObjectURL(file);
    setPreviewURL(url);
    setWasCleared(false);

    // Update react-hook-form
    onChange(file);

    if (videoRef.current) {
      videoRef.current.srcObject = null;
      videoRef.current.src = url;
      videoRef.current.controls = true;
      videoRef.current.muted = false;

      // Add error handling for play()
      videoRef.current.play().catch((error) => {
        // This is normal when switching between streams
      });
    }
    setWasCleared(false);

    // Update react-hook-form
    onChange(file);

    // Clear the file input to allow selecting the same file again
    e.target.value = "";
  };

  // Determine which video to show (priority: uploaded > recorded > initial URL > none)
  const videoToShow = uploadedVideoUrl
    ? typeof uploadedVideoUrl === "string"
      ? uploadedVideoUrl
      : previewURL
    : previewURL;
  const hasVideo = !wasCleared && (!!videoToShow || hasInitialVideo);

  return (
    <div className={`flex flex-col gap-4 items-center ${className}`}>
      {/* Video Container */}
      <div
        className={`relative border rounded-lg overflow-hidden ${
          responsive
            ? "w-full max-w-md md:max-w-lg lg:max-w-xl aspect-video"
            : ""
        }`}
        style={!responsive ? { width, height } : undefined}
      >
        {/* Always show video element, but control its content */}
        <video
          ref={videoRef}
          src={videoToShow || initialVideoUrl || undefined}
          autoPlay={recording}
          muted={recording}
          width={responsive ? undefined : width}
          height={responsive ? undefined : height}
          className="w-full h-full object-cover"
          controls={!recording && hasVideo}
        />

        {/* Show placeholder when no video and not recording */}
        {!hasVideo && !recording && (
          <div className="absolute inset-0 bg-gray-200 flex items-center justify-center">
            <div className="text-center text-gray-500">
              <div className="text-4xl mb-2">📹</div>
              <p className="text-sm">{t("noVideo")}</p>
            </div>
          </div>
        )}

        {/* Recording indicator overlay */}
        {recording && (
          <div className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2">
            <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
            {t("recording")} {formatTime(recordingTime)}
          </div>
        )}

        {/* Record button overlay */}
        {(!hasVideo || recording) && (
          <div>
            {!recording && (
              <button
                type="button"
                onClick={startRecording}
                className="absolute bottom-1 md:bottom-4  left-1/2 -translate-x-1/2 bg-white rounded-full p-[5px] transition duration-200 shadow-xl ring-1 ring-gray-300 hover:ring-red-500 hover:scale-105 cursor-pointer"
              >
                <div className="w-9 h-9 bg-red-500 rounded-full"></div>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col gap-3 w-full max-w-md">
        {/* Record/Stop Button - Only show when no video or during recording */}
        {(!hasVideo || recording) && (
          <div className="flex justify-center">
            {recording && (
              <Button
                onClick={stopRecording}
                variant="danger"
                className="w-full md:max-w-[298px]"
              >
                {t("stopRecording")}
              </Button>
            )}
          </div>
        )}

        {/* Video Actions - Show when video exists and not recording */}
        {hasVideo && !recording && (
          <div className="flex justify-center">
            <Button
              onClick={clearCurrentVideo}
              variant="danger"
              className="w-full md:max-w-[298px]"
            >
              {t("removeVideo")}
            </Button>
          </div>
        )}

        {/* File Upload */}
        <div className="mt-2 w-full">
          <div className="mb-3">
            <h3 className="font-bold text-black text-md mb-1">
              {t("uploadYours")}
            </h3>
            <p className="text-sm text-gray-600">{t("uploadInstructions")}</p>
          </div>

          <label className="block w-full">
            <input
              type="file"
              accept="video/mp4,video/mpeg,video/webm"
              onChange={handleUpload}
              className="hidden"
            />
            <div
              className={`border-2 border-dashed border-gray-300 rounded-lg py-2 text-center cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-colors duration-200 ${
                isDragOver ? "border-blue-500 bg-blue-50" : ""
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="flex flex-col items-center">
                {/* Upload Icon */}
                <div className="w-12 h-12 flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                </div>

                {/* Call to Action Text */}
                <div className="text-gray-500">
                  <p className="font-medium">{t("dropFiles")}</p>
                  <p className="text-xs mt-1">{t("clickToBrowse")}</p>
                </div>
              </div>
            </div>
          </label>

          <p className="text-xs text-gray-500 mt-2 text-center">
            {t("acceptedFormats")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default VideoRecorderUploader;
